import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Grid,
    CircularProgress,
    Alert,
    Stepper,
    Step,
    StepLabel,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemSecondary,
    Chip
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    Check as CheckIcon,
    DomainVerification as DomainIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const steps = ['Inserisci Dominio', 'Scegli Metodo di Verifica', 'Verifica Proprietà'];

const DomainVerificationPage = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [domain, setDomain] = useState('');
    const [verificationMethod, setVerificationMethod] = useState('');
    const [verificationToken, setVerificationToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [verifiedDomains, setVerifiedDomains] = useState([]);
    const [copied, setCopied] = useState(false);
    const theme = useTheme();

    // Carica i domini verificati all'avvio
    useEffect(() => {
        fetchVerifiedDomains();
    }, []);

    const fetchVerifiedDomains = async () => {
        try {
            const response = await axios.get(`${API_URL}/domains/verified`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            setVerifiedDomains(response.data.domains || []);
        } catch (error) {
            console.error('Error fetching verified domains:', error);
            setError('Errore nel caricamento dei domini verificati');
        }
    };

    const handleNext = () => {
        if (activeStep === 0 && !domain) {
            setError('Inserisci un dominio valido');
            return;
        }
        if (activeStep === 1 && !verificationMethod) {
            setError('Seleziona un metodo di verifica');
            return;
        }
        setActiveStep((prevStep) => prevStep + 1);
        setError(null);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
        setError(null);
    };

    const handleDomainChange = (event) => {
        setDomain(event.target.value.trim());
        setError(null);
    };

    const generateToken = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.post(
                `${API_URL}/domains/generate-token`,
                { domain },
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );
            setVerificationToken(response.data.token);
            setSuccess('Token generato con successo');
        } catch (error) {
            console.error('Error generating token:', error);
            setError(error.response?.data?.error || 'Errore nella generazione del token');
        } finally {
            setLoading(false);
        }
    };

    const verifyDomain = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.post(
                `${API_URL}/domains/verify`,
                {
                    domain,
                    method: verificationMethod
                },
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );
            setSuccess('Dominio verificato con successo');
            await fetchVerifiedDomains();
            resetForm();
        } catch (error) {
            console.error('Error verifying domain:', error);
            setError(error.response?.data?.error || 'Errore nella verifica del dominio');
        } finally {
            setLoading(false);
        }
    };

    const deleteDomain = async (domainId) => {
        try {
            setLoading(true);
            await axios.delete(`${API_URL}/domains/${domainId}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            await fetchVerifiedDomains();
            setSuccess('Dominio eliminato con successo');
        } catch (error) {
            console.error('Error deleting domain:', error);
            setError('Errore nell\'eliminazione del dominio');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const resetForm = () => {
        setActiveStep(0);
        setDomain('');
        setVerificationMethod('');
        setVerificationToken('');
        setError(null);
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Dominio"
                            variant="outlined"
                            value={domain}
                            onChange={handleDomainChange}
                            placeholder="esempio.com"
                            helperText="Inserisci il dominio da verificare"
                        />
                    </Box>
                );
            case 1:
                return (
                    <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Card 
                                    sx={{ 
                                        cursor: 'pointer',
                                        border: theme => verificationMethod === 'dns' ? `2px solid ${theme.palette.primary.main}` : '1px solid rgba(255, 255, 255, 0.12)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: theme => theme.palette.primary.main,
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                    onClick={() => {
                                        setVerificationMethod('dns');
                                        setError(null);
                                    }}
                                >
                                    <CardContent>
                                        <Typography variant="h6">Verifica DNS</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Aggiungi un record TXT al tuo DNS
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Card 
                                    sx={{ 
                                        cursor: 'pointer',
                                        border: theme => verificationMethod === 'file' ? `2px solid ${theme.palette.primary.main}` : '1px solid rgba(255, 255, 255, 0.12)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: theme => theme.palette.primary.main,
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                    onClick={() => {
                                        setVerificationMethod('file');
                                        setError(null);
                                    }}
                                >
                                    <CardContent>
                                        <Typography variant="h6">Verifica File</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Carica un file di verifica sul tuo server
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                );
            case 2:
                return (
                    <Box sx={{ mt: 2 }}>
                        {!verificationToken ? (
                            <Button
                                variant="contained"
                                onClick={generateToken}
                                disabled={loading}
                            >
                                Genera Token di Verifica
                            </Button>
                        ) : (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Token di Verifica:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        value={verificationToken}
                                        variant="outlined"
                                        InputProps={{ readOnly: true }}
                                    />
                                    <Tooltip title={copied ? "Copiato!" : "Copia"}>
                                        <IconButton onClick={() => copyToClipboard(verificationToken)}>
                                            {copied ? <CheckIcon /> : <CopyIcon />}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                {verificationMethod === 'dns' ? (
                                    <Alert severity="info">
                                        Aggiungi questo record TXT al tuo DNS:<br />
                                        <strong>Nome:</strong> _exploit-finder-verification.{domain}<br />
                                        <strong>Valore:</strong> {verificationToken}
                                    </Alert>
                                ) : (
                                    <Alert severity="info">
                                        Crea un file con questo nome nella root del tuo dominio:<br />
                                        <strong>exploit-finder-{verificationToken}.txt</strong>
                                    </Alert>
                                )}
                            </Box>
                        )}
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
                Verifica Domini
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {success && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {success}
                            </Alert>
                        )}

                        {renderStepContent(activeStep)}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                            <Button
                                disabled={activeStep === 0}
                                onClick={handleBack}
                            >
                                Indietro
                            </Button>
                            <Box>
                                {activeStep === steps.length - 1 ? (
                                    <Button
                                        variant="contained"
                                        onClick={verifyDomain}
                                        disabled={loading || !verificationToken}
                                    >
                                        {loading ? <CircularProgress size={24} /> : 'Verifica'}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        onClick={handleNext}
                                    >
                                        Avanti
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Domini Verificati
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <List>
                            {verifiedDomains.map((domain) => (
                                <ListItem
                                    key={domain.id}
                                    secondaryAction={
                                        <Tooltip title="Elimina dominio">
                                            <IconButton 
                                                edge="end" 
                                                onClick={() => deleteDomain(domain.id)}
                                                disabled={loading}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    }
                                >
                                    <ListItemText
                                        primary={domain.domain}
                                        secondary={
                                            <Chip
                                                size="small"
                                                label={domain.status}
                                                color={domain.status === 'verified' ? 'success' : 'warning'}
                                            />
                                        }
                                    />
                                </ListItem>
                            ))}
                            {verifiedDomains.length === 0 && (
                                <Typography color="text.secondary" align="center">
                                    Nessun dominio verificato
                                </Typography>
                            )}
                        </List>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DomainVerificationPage; 